
import { Component, Vue } from "vue-property-decorator";
import EmblaCarousel, { EmblaCarouselType } from "embla-carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { homeReviews, HomeReview } from "@/data/homeReviews";

@Component
export default class ReviewsMarquee extends Vue {
  reviews: ReadonlyArray<HomeReview> = homeReviews;
  prefersReducedMotion = false;
  emblaApis: Array<EmblaCarouselType | null> = [null, null];
  motionMediaQuery: MediaQueryList | null = null;
  viewportObserver: IntersectionObserver | null = null;
  isPaused = false;
  isInViewport = false;
  isDocumentVisible = true;

  mounted(): void {
    this.motionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.prefersReducedMotion = this.motionMediaQuery.matches;
    this.isPaused = this.prefersReducedMotion;
    this.motionMediaQuery.addListener(this.handleMotionPreferenceChange);
    this.isDocumentVisible = !document.hidden;
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    const marqueeShell = this.$refs.marqueeShell as HTMLElement | undefined;
    if (marqueeShell && "IntersectionObserver" in window) {
      this.viewportObserver = new IntersectionObserver(
        this.handleViewportIntersection,
        { threshold: 0.01, rootMargin: "120px 0px" }
      );
      this.viewportObserver.observe(marqueeShell);
    } else {
      this.isInViewport = true;
    }
    this.$nextTick(this.initializeCarousels);
  }

  beforeDestroy(): void {
    if (this.motionMediaQuery) {
      this.motionMediaQuery.removeListener(this.handleMotionPreferenceChange);
    }
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.viewportObserver?.disconnect();
    this.viewportObserver = null;
    this.destroyCarousels();
  }

  get isPlaybackActive(): boolean {
    return !this.isPaused && this.isInViewport && this.isDocumentVisible;
  }

  get playbackLabel(): string {
    return this.isPaused ? "播放客戶評價" : "暫停客戶評價";
  }

  get reviewRows(): HomeReview[][] {
    return [
      this.reviews.filter((_, index) => index % 2 === 0),
      this.reviews.filter((_, index) => index % 2 === 1),
    ];
  }

  starIcon(stars: number, position: number): string[] {
    if (stars >= position) return ["fas", "star"];
    if (stars >= position - 0.5) return ["fas", "star-half-alt"];
    return ["far", "star"];
  }

  handleMotionPreferenceChange(event: MediaQueryListEvent): void {
    this.prefersReducedMotion = event.matches;
    if (event.matches) {
      this.isPaused = true;
    }
    this.syncCarouselPlayback();
  }

  handleViewportIntersection(entries: IntersectionObserverEntry[]): void {
    this.isInViewport = entries.some((entry) => entry.isIntersecting);
    this.syncCarouselPlayback();
  }

  handleVisibilityChange(): void {
    this.isDocumentVisible = !document.hidden;
    this.syncCarouselPlayback();
  }

  togglePlayback(): void {
    this.isPaused = !this.isPaused;
    this.syncCarouselPlayback();
  }

  initializeCarousels(): void {
    this.destroyCarousels();
    this.emblaApis = this.reviewTrackElements.map((track, rowIndex) => {
      const api = EmblaCarousel(
        track,
        {
          align: "start",
          dragFree: true,
          loop: true,
          watchDrag: false,
          breakpoints: {
            "(max-width: 768px)": {
              watchDrag: true,
            },
          },
        },
        [
          AutoScroll({
            direction: rowIndex === 0 ? "forward" : "backward",
            playOnInit: false,
            speed: rowIndex === 0 ? 0.25 : 0.23,
            startDelay: 2000,
            stopOnFocusIn: true,
            stopOnInteraction: false,
            stopOnMouseEnter: false,
            breakpoints: {
              "(max-width: 768px)": {
                speed: rowIndex === 0 ? 0.22 : 0.2,
              },
            },
          }),
        ]
      );
      api.on("reInit", this.syncCarouselPlayback);
      return api;
    });
    this.syncCarouselPlayback();
  }

  syncCarouselPlayback(): void {
    this.emblaApis.forEach((api) => {
      const autoScroll = api?.plugins().autoScroll;
      if (!autoScroll) return;

      if (this.isPlaybackActive) {
        if (!autoScroll.isPlaying()) {
          autoScroll.play(0);
          // Restore the interaction delay after starting this playback immediately.
          autoScroll.play(2000);
        }
      } else {
        autoScroll.stop();
      }
    });
  }

  destroyCarousels(): void {
    this.emblaApis.forEach((api) => api?.destroy());
    this.emblaApis = [null, null];
  }

  get reviewTrackElements(): HTMLElement[] {
    const tracks = this.$refs.reviewTracks as
      | HTMLElement
      | HTMLElement[]
      | undefined;
    if (!tracks) return [];
    return Array.isArray(tracks) ? tracks : [tracks];
  }
}
